<template>
    <CustomDialog
        v-model="isVisible"
        :defaultTitle="isEdit ? $t('productBrand.editPopup.title') : $t('productBrand.createPopup.title')"
        width="800px"
    >
        <template #content>
            <div class="ppv2-p-8">
                <div class="ppv2-flex ppv2-flex-col lg:ppv2-flex-row lg:ppv2-justify-between ppv2-gap-8">
                    <!-- Name -->
                    <CustomInput
                        v-model="form.name"
                        :label="$t('productBrand.createPopup.nameLabel')"
                        :placeholder="$t('productBrand.createPopup.namePlaceholder')"
                        required
                        :state="getState('name')"
                        :errorText="errors.name ? errors.name[0] : ''"
                        @input="clearError('name')"
                        classContainerAdd="ppv2-flex-1"
                    />
                    <!-- Email -->
                    <CustomInput
                        v-model="form.email"
                        :label="$t('productBrand.createPopup.emailLabel')"
                        :placeholder="$t('productBrand.createPopup.emailPlaceholder')"
                        :state="getState('email')"
                        @input="clearError('email')"
                        :errorText="errors.email ? errors.email[0] : ''"
                        classContainerAdd="ppv2-flex-1"
                    />
                </div>
                <div class="ppv2-flex ppv2-flex-col lg:ppv2-flex-row lg:ppv2-justify-between ppv2-gap-8">
                    <!-- Phone -->
                    <CustomInput
                        v-model="form.phone_number"
                        :label="$t('productBrand.createPopup.phoneLabel')"
                        :placeholder="$t('productBrand.createPopup.phonePlaceholder')"
                        :state="getState('phone_number')"
                        @input="clearError('phone_number')"
                        :errorText="errors.phone_number ? errors.phone_number[0] : ''"
                        classContainerAdd="ppv2-flex-1"
                    />
                    <!-- Suppliers -->
                    <BaseSelect
                        v-model="form.suppliers"
                        label="Fournisseurs de la marque"
                        placeholder="Associez un ou plusieurs fournisseurs"
                        :options="suppliersOptions"
                        optionLabel="name"
                        optionValue="id"
                        :multiple="true"
                        size="sm"
                        customLabelClass="poppinsLight"
                        customInputClass="!ppv2-mb-[20px] ppv2-min-w-[100px]"
                        classContainerAdded="ppv2-flex-1"
                    />
                </div>
            </div>
        </template>

        <template #actions>
            <BaseButton
                variant="primary"
                @click="submit"
                :disabled="isLoading"
                :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                >{{ $t('common.confirm') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>
<script>
import BaseSelect from '../../../Shared/BaseSelect.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'

export default {
    name: 'BrandForm',
    components: {
        CustomDialog,
        BaseButton,
        CustomInput,
        BaseSelect,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        editItem: {
            type: Object,
            default: null,
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isLoading: false,
            form: {
                name: '',
                email: '',
                phone_number: '',
                suppliers: [],
            },
            suppliersOptions: [],
            errors: {},
        }
    },
    computed: {
        isEdit() {
            return !!this.editItem
        },
        isVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
    },
    watch: {
        editItem: {
            handler(newVal) {
                if (newVal) {
                    this.form.name = newVal.name || ''
                    this.form.email = newVal.email || ''
                    this.form.phone_number = newVal.phone_number || ''
                    this.form.suppliers = newVal.suppliers ? newVal.suppliers.map((s) => s.id) : []
                } else {
                    this.form.name = ''
                    this.form.email = ''
                    this.form.phone_number = ''
                    this.form.suppliers = []
                }
            },
            immediate: true,
        },
        isVisible(newVal) {
            if (newVal && !this.isEdit) {
                // Reset form for create mode when opening
                this.form.name = ''
                this.form.email = ''
                this.form.phone_number = ''
                this.form.suppliers = []
                this.errors = {}
            }
        },
    },
    created() {
        if (this.editItem) {
            this.form.name = this.editItem.name || ''
            this.form.email = this.editItem.email || ''
            this.form.phone_number = this.editItem.phone_number || ''
            this.form.suppliers = this.editItem.suppliers ? this.editItem.suppliers.map((s) => s.id) : []
        }
        this.fetchSuppliers()
    },
    methods: {
        normalizeSize,
        getState(field) {
            if (this.errors[field]) return 'error'
            return 'default'
        },
        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },
        validateForm() {
            const errors = {}

            if (!this.form.name || this.form.name.trim() === '') {
                errors.name = [this.$t('productBrand.validation.nameRequired')]
            }

            return errors
        },
        async submit() {
            this.errors = this.validateForm()

            if (Object.keys(this.errors).length > 0) {
                const firstError = Object.values(this.errors)[0][0]
                this.$toast.error(firstError)
                return
            }

            this.isLoading = true
            this.errors = {}

            try {
                let endpoint = ''
                let method = 'POST'

                if (this.isEdit) {
                    endpoint = `/api/v3/brand/update/${this.editItem.id}`
                    method = 'PATCH'
                } else {
                    endpoint = '/api/v3/brand/store'
                }

                const response = await axios({
                    method: method,
                    url: endpoint,
                    data: this.form,
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                if (response.data.success) {
                    this.$toast.success(this.isEdit ? 'Modifié avec succès' : this.$t('productBrand.createPopup.success'))
                    this.$emit('success', response.data.data)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    const firstError = Object.values(this.errors)[0][0]
                    this.$toast.error(firstError)
                } else {
                    this.$toast.error('Une erreur est survenue')
                }
            } finally {
                this.isLoading = false
            }
        },
        async fetchSuppliers() {
            try {
                const response = await axios.get('/api/v3/brand/fetch-suppliers', {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                if (response.data.success) {
                    this.suppliersOptions = response.data.data
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching suppliers:', error)
            }
        },
    },
}
</script>
